/**
 * 从 aimeta_android 注入的系统登录态同步到 Web（仅手机 App WebView）。
 * 数据来源：AimetaManager（见 aimeta_system_api.md）→ Android 注入 window.__AIMETA_*。
 * Windows 客户端、普通浏览器不走此逻辑。
 */

import { useUserStore } from '@/stores/user';
import {
  getAimetaNativeBridge,
  isAimetaAndroidWebView,
  isAimetaManagerReachable,
  shouldUseServerActivationState,
  shouldUseWebLoginOnMobile,
} from '@/utils/platform';

export { shouldUseServerActivationState };

export interface NativeLoginSnapshot {
  token: string;
  accountName: string;
  deviceCode: string;
  deviceName: string;
  serverUrl: string;
  isVip: boolean;
  expireTime: number;
  deviceActivated: boolean;
}

const LOGIN_NAME_KEY = 'loginName';
const SERVER_URL_KEY = 'ttsServerUrl';

/**
 * account_info_v2：isExpiry 为 0 表示未到期；或 expireTime（秒）晚于当前时间（设备槽位有效）。
 */
export function isAccountActiveFromInfo(data: {
  isExpiry?: number | boolean | null;
  expireTime?: number | string | null;
  isInternal?: number | boolean | null;
}): boolean {
  const expiry = Number(data?.expireTime);
  if (expiry === -1) {
    return true;
  }
  const nowSec = Math.floor(Date.now() / 1000);
  if (Number.isFinite(expiry) && expiry > nowSec) {
    return true;
  }
  return data?.isExpiry === 0 || data?.isExpiry === false;
}

/** 将 account_info_v2 激活态写入 Pinia / localStorage */
export function applyActivationFromAccountInfo(data: {
  isExpiry?: number | boolean | null;
  expireTime?: number | string | null;
}): boolean {
  const active = isAccountActiveFromInfo(data);
  const userStore = useUserStore();
  userStore.setVip(active);
  localStorage.setItem('deviceActivated', active ? '1' : '0');
  return active;
}

/** 将 account_info_v2 结果写入 Pinia / localStorage（与浏览器一致） */
export function applyProfileFromAccountInfo(data: {
  loginName?: string | null;
  accountName?: string | null;
  isExpiry?: number | boolean | null;
  expireTime?: number | string | null;
  isDeveloper?: number | boolean | null;
  permissions?: string[] | null;
}): boolean {
  const name = String(data?.loginName || data?.accountName || '').trim();
  if (name) {
    localStorage.setItem(LOGIN_NAME_KEY, name);
  }
  const userStore = useUserStore();
  userStore.setDeveloperProfile({
    isDeveloper: data?.isDeveloper,
    permissions: Array.isArray(data?.permissions) ? data.permissions : [],
  });
  return applyActivationFromAccountInfo(data);
}

const DEVICE_CODE_LEN = 16;
const DEVICE_CODE_HEAD_LEN = 12;

/** ROM：16 位，前 12 位 hex，末 4 位数字 */
function isValidRomDeviceCode(code: string): boolean {
  if (code.length !== DEVICE_CODE_LEN) return false;
  const head = code.slice(0, DEVICE_CODE_HEAD_LEN);
  const tail = code.slice(DEVICE_CODE_HEAD_LEN);
  return /^[0-9a-f]+$/.test(head) && /^[0-9]{4}$/.test(tail);
}

function isValidDeviceCodeHex(code: string): boolean {
  return /^[0-9a-f]{32}$/.test(code);
}

function isValidNativeDeviceCode(code: string): boolean {
  if (isValidRomDeviceCode(code)) return true;
  if (isValidDeviceCodeHex(code)) return true;
  return code.startsWith('develop-') && code.length > 8;
}

/**
 * 与 Settings / AimetaSystemClient.readDeviceCode / persist 一致。
 * AimetaManager 可达时只信 bridge.getDeviceInfo 与 __AIMETA_DEVICE_CODE，不信 localStorage 旧值。
 */
export function getCanonicalDeviceCodeFromNative(fromSnapshot?: string): string {
  if (!isAimetaAndroidWebView()) return '';

  if (isAimetaManagerReachable()) {
    try {
      const bridge = getAimetaNativeBridge();
      if (bridge?.getDeviceInfo) {
        const parsed = JSON.parse(bridge.getDeviceInfo());
        const fromBridge = String(parsed?.deviceCode ?? '').trim();
        if (isValidNativeDeviceCode(fromBridge)) return fromBridge;
      }
    } catch {
      /* ignore */
    }
    const fromWindow = readStringGlobal('__AIMETA_DEVICE_CODE' as keyof Window).trim();
    if (isValidDeviceCodeHex(fromWindow)) return fromWindow;
    const snap = fromSnapshot?.trim() ?? '';
    if (isValidDeviceCodeHex(snap)) return snap;
    return '';
  }

  const candidates: string[] = [];
  if (fromSnapshot?.trim()) candidates.push(fromSnapshot.trim());
  const fromWindow = readStringGlobal('__AIMETA_DEVICE_CODE' as keyof Window).trim();
  if (fromWindow) candidates.push(fromWindow);
  try {
    const bridge = getAimetaNativeBridge();
    if (bridge?.getDeviceInfo) {
      const parsed = JSON.parse(bridge.getDeviceInfo());
      const fromBridge = String(parsed?.deviceCode ?? '').trim();
      if (fromBridge) candidates.push(fromBridge);
    }
  } catch {
    /* ignore */
  }
  return candidates.find((c) => isValidNativeDeviceCode(c)) ?? candidates[0] ?? '';
}

/**
 * 设备码以 AimetaManager / Android 注入为准，覆盖 Web localStorage 中的旧值。
 * @param fromSnapshot 注入事件或 readNativeLoginSnapshot 中的 deviceCode
 */
export function reconcileDeviceCodeWithNative(fromSnapshot?: string): void {
  if (!isAimetaAndroidWebView()) return;
  const systemCode = getCanonicalDeviceCodeFromNative(fromSnapshot);
  if (!systemCode) return;
  const userStore = useUserStore();
  const prev = (userStore.deviceCode || localStorage.getItem('deviceCode') || '').trim();
  if (prev && prev !== systemCode) {
    console.warn('[Aimeta] deviceCode 已与系统对齐', { prev: prev.slice(0, 12), system: systemCode.slice(0, 12) });
  }
  userStore.setDeviceCode(systemCode);
  localStorage.setItem('deviceCode', systemCode);
}

/** 与 Settings / AimetaManager getDeviceName 一致；有 Manager 时优先 bridge 注入。 */
export function getCanonicalDeviceNameFromNative(fromSnapshot?: string): string {
  if (!isAimetaAndroidWebView()) return '';

  if (isAimetaManagerReachable()) {
    try {
      const bridge = getAimetaNativeBridge();
      if (bridge?.getDeviceInfo) {
        const parsed = JSON.parse(bridge.getDeviceInfo());
        const fromBridge = String(parsed?.deviceName ?? '').trim();
        if (fromBridge) return fromBridge;
      }
    } catch {
      /* ignore */
    }
    const fromWindow = readStringGlobal('__AIMETA_DEVICE_NAME' as keyof Window).trim();
    if (fromWindow) return fromWindow;
    const snap = fromSnapshot?.trim() ?? '';
    if (snap) return snap;
    return '';
  }

  const candidates: string[] = [];
  if (fromSnapshot?.trim()) candidates.push(fromSnapshot.trim());
  const fromWindow = readStringGlobal('__AIMETA_DEVICE_NAME' as keyof Window).trim();
  if (fromWindow) candidates.push(fromWindow);
  try {
    const bridge = getAimetaNativeBridge();
    if (bridge?.getDeviceInfo) {
      const parsed = JSON.parse(bridge.getDeviceInfo());
      const fromBridge = String(parsed?.deviceName ?? '').trim();
      if (fromBridge) candidates.push(fromBridge);
    }
  } catch {
    /* ignore */
  }
  return candidates[0] ?? '';
}

export function reconcileDeviceNameWithNative(fromSnapshot?: string): void {
  if (!isAimetaAndroidWebView()) return;
  const systemName = getCanonicalDeviceNameFromNative(fromSnapshot);
  if (!systemName) return;
  const userStore = useUserStore();
  const prev = (userStore.deviceName || localStorage.getItem('deviceName') || '').trim();
  if (prev && prev !== systemName) {
    console.warn('[Aimeta] deviceName 已与系统对齐', { prev, system: systemName });
  }
  userStore.setDeviceName(systemName);
  localStorage.setItem('deviceName', systemName);
}

/** Android 注入的账号名（仅作 API 失败时的展示兜底，不含积分） */
export function readNativeAccountName(): string {
  if (typeof window === 'undefined') return '';
  const fromWindow = readStringGlobal('__AIMETA_ACCOUNT_NAME' as keyof Window);
  if (fromWindow) return fromWindow;
  return localStorage.getItem(LOGIN_NAME_KEY)?.trim() || '';
}

function parseMaybeJsonString(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  if (
    (trimmed.startsWith('"') && trimmed.endsWith('"')) ||
    (trimmed.startsWith("'") && trimmed.endsWith("'"))
  ) {
    try {
      return String(JSON.parse(trimmed));
    } catch {
      return trimmed.slice(1, -1);
    }
  }
  return trimmed;
}

function readStringGlobal(key: keyof Window): string {
  const value = (window as any)[key];
  return typeof value === 'string' ? value.trim() : String(value ?? '').trim();
}

/** 读取 Android 注入的登录快照；非手机端返回 null */
export function readNativeLoginSnapshot(): NativeLoginSnapshot | null {
  if (!isAimetaAndroidWebView()) return null;

  let token = readStringGlobal('__AIMETA_TOKEN' as keyof Window);
  let accountName = readStringGlobal('__AIMETA_ACCOUNT_NAME' as keyof Window);
  let deviceCode = readStringGlobal('__AIMETA_DEVICE_CODE' as keyof Window);
  let deviceName = readStringGlobal('__AIMETA_DEVICE_NAME' as keyof Window);
  let serverUrl = readStringGlobal('__AIMETA_SERVER_URL' as keyof Window);
  let isVip = !!(window as any).__AIMETA_VIP;
  let expireTime = Number((window as any).__AIMETA_EXPIRE_TIME) || 0;
  // 激活态仅来自 AimetaManager（Android 注入 __AIMETA_DEVICE_ACTIVATED）
  let deviceActivated = !!(window as any).__AIMETA_DEVICE_ACTIVATED;

  try {
    const bridge = getAimetaNativeBridge();
    if (bridge && typeof bridge.getDeviceInfo === 'function') {
      const raw = bridge.getDeviceInfo();
      const parsed = typeof raw === 'string' ? JSON.parse(raw || '{}') : raw;
      if (!token && parsed?.token) token = String(parsed.token).trim();
      if (!accountName && parsed?.accountName) {
        accountName = String(parsed.accountName).trim();
      }
      if (!deviceCode && parsed?.deviceCode) {
        deviceCode = String(parsed.deviceCode).trim();
      }
      if (!deviceName && parsed?.deviceName) {
        deviceName = String(parsed.deviceName).trim();
      }
      if (parsed?.deviceActivated != null) {
        deviceActivated = !!parsed.deviceActivated;
      }
      if (parsed?.expireTime != null) expireTime = Number(parsed.expireTime) || 0;
    }
  } catch {
    /* ignore */
  }

  // 槽位未激活时，若 expireTime 仍有效则允许开播（与 account_info_v2 一致；-1 为永久）
  const nowSec = Math.floor(Date.now() / 1000);
  if (!deviceActivated && (expireTime === -1 || expireTime > nowSec)) {
    deviceActivated = true;
  }
  isVip = deviceActivated;

  return {
    token: parseMaybeJsonString(token),
    accountName: parseMaybeJsonString(accountName),
    deviceCode: parseMaybeJsonString(deviceCode),
    deviceName: parseMaybeJsonString(deviceName),
    serverUrl: parseMaybeJsonString(serverUrl),
    isVip,
    expireTime,
    deviceActivated,
  };
}

/** 将快照写入 Pinia + localStorage；非手机端不执行 */
export function applyNativeLoginSnapshot(
  snapshot: NativeLoginSnapshot,
  options?: { clearIfEmpty?: boolean }
): boolean {
  if (!isAimetaAndroidWebView()) return false;

  const userStore = useUserStore();
  const clearIfEmpty = options?.clearIfEmpty ?? false;

  if (!snapshot.token) {
    if (clearIfEmpty && !userStore.token) {
      userStore.clearToken();
      userStore.setVip(false);
      localStorage.removeItem('token');
      localStorage.removeItem(LOGIN_NAME_KEY);
      localStorage.removeItem('deviceActivated');
    }
    return false;
  }

  userStore.setToken(snapshot.token);
  localStorage.setItem('token', JSON.stringify(snapshot.token));

  if (snapshot.accountName) {
    localStorage.setItem(LOGIN_NAME_KEY, snapshot.accountName);
  }

  userStore.setVip(snapshot.deviceActivated);
  localStorage.setItem('deviceActivated', snapshot.deviceActivated ? '1' : '0');

  reconcileDeviceCodeWithNative(snapshot.deviceCode);
  reconcileDeviceNameWithNative(snapshot.deviceName);
  if (snapshot.serverUrl) {
    userStore.setTtsUrl(snapshot.serverUrl);
    localStorage.setItem(SERVER_URL_KEY, snapshot.serverUrl);
  }

  return true;
}

/** 从 window 读取并应用；仅手机端有效 */
export function syncNativeLoginFromWindow(): boolean {
  if (!isAimetaAndroidWebView()) return false;
  const snapshot = readNativeLoginSnapshot();
  if (!snapshot) return false;
  return applyNativeLoginSnapshot(snapshot);
}

/**
 * 页面加载后延迟重试（Android 可能在 Vue 启动后才注入 token）。
 * 仅手机端调用。
 */
export function scheduleNativeLoginSyncRetry(
  delaysMs: number[] = [0, 300, 800, 1500, 3000]
): void {
  if (!isAimetaAndroidWebView()) return;
  for (const delay of delaysMs) {
    window.setTimeout(() => {
      if (!isAimetaAndroidWebView()) return;
      const synced = syncNativeLoginFromWindow();
      if (synced) {
        window.dispatchEvent(new CustomEvent('aimeta:native-login-ready'));
      }
    }, delay);
  }
}

let listenerInstalled = false;

/** 监听 Android 页面完成后的二次注入（仅手机端注册） */
export function setupNativeLoginSyncListener(): void {
  if (!isAimetaAndroidWebView() || listenerInstalled) return;
  listenerInstalled = true;

  window.addEventListener('aimeta:native-login-sync', ((event: CustomEvent) => {
    if (!isAimetaAndroidWebView()) return;
    const detail = event?.detail;
    if (!detail || typeof detail !== 'object') {
      syncNativeLoginFromWindow();
      return;
    }
    const snap = {
      token: String(detail.token ?? '').trim(),
      accountName: String(detail.accountName ?? '').trim(),
      deviceCode: String(detail.deviceCode ?? '').trim(),
      deviceName: String(detail.deviceName ?? '').trim(),
      serverUrl: String(detail.serverUrl ?? '').trim(),
      expireTime: Number(detail.expireTime) || 0,
      deviceActivated: !!detail.deviceActivated,
      isVip: !!detail.deviceActivated,
    };
    applyNativeLoginSnapshot(snap, { clearIfEmpty: true });
    reconcileDeviceCodeWithNative(snap.deviceCode);
    reconcileDeviceNameWithNative(snap.deviceName);
    if (snap.token) {
      window.dispatchEvent(new CustomEvent('aimeta:native-login-ready'));
    }
  }) as EventListener);
}

export function isNativeLoginSynced(): boolean {
  if (!isAimetaAndroidWebView()) return false;
  const userStore = useUserStore();
  return !!userStore.token;
}

/** 清理 WebView 内残留的 Web 登录态（localStorage + Pinia） */
export function clearWebLoginStorage(): void {
  const userStore = useUserStore();
  userStore.clearToken();
  userStore.setVip(false);
  localStorage.removeItem('token');
  localStorage.removeItem(LOGIN_NAME_KEY);
  localStorage.removeItem('deviceActivated');
}

/**
 * 无 AimetaManager 时：仅以 Android 注入的 token 为准，并清理过期 localStorage。
 * @returns 当前是否已登录
 */
export function enforceWebLoginFromNative(): boolean {
  if (!shouldUseWebLoginOnMobile()) {
    return isNativeLoginSynced() || !!localStorage.getItem('token');
  }
  syncNativeLoginFromWindow();
  const snapshot = readNativeLoginSnapshot();
  const token = (snapshot?.token || useUserStore().token || '').trim();
  if (!token) {
    clearWebLoginStorage();
    return false;
  }
  if (snapshot) {
    applyNativeLoginSnapshot(snapshot);
  }
  return true;
}

export function isWebLoginRoutePath(path: string): boolean {
  return path === '/' || path === '/resetPassword';
}

let lastSystemAccountLoginMs = 0;
const SYSTEM_ACCOUNT_LOGIN_DEBOUNCE_MS = 2500;

/** 有 AimetaManager 时拉起系统 Settings 账号中心（勿走 Web 登录页） */
export function openSystemAccountLoginOnNative(force = false): boolean {
  if (!isAimetaAndroidWebView() || !isAimetaManagerReachable()) {
    return false;
  }
  const now = Date.now();
  if (!force && now - lastSystemAccountLoginMs < SYSTEM_ACCOUNT_LOGIN_DEBOUNCE_MS) {
    return true;
  }
  try {
    const bridge = getAimetaNativeBridge() as
      | { openSystemAccountLogin?: () => void }
      | undefined;
    if (typeof bridge?.openSystemAccountLogin === 'function') {
      bridge.openSystemAccountLogin();
      lastSystemAccountLoginMs = now;
      return true;
    }
  } catch {
    /* ignore */
  }
  return false;
}

/** 会话失效：有 Manager 则系统账号中心，否则清理 Web 登录态 */
export function handleNativeSessionLost(): void {
  clearWebLoginStorage();
  if (openSystemAccountLoginOnNative(true)) {
    return;
  }
  if (shouldUseWebLoginOnMobile()) {
    const path = window.location?.pathname || '/';
    if (!isWebLoginRoutePath(path)) {
      window.location.replace('/');
    }
  }
}
